import { useMemo, useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { GET_TMS_VEHICLES_WITH_MODEL, TmsVehiclesWithModelResponse } from '../schedule/graphql/queries/getTmsVehiclesWithModel';
import { useTmsDriversByRoute } from './useTmsDriversByRoute';
import { useTmsSearch } from './useTmsSearch';
import { TmsVehicleStatus } from 'enums/TmsEnum';

/**
 * 根据 routeId 自动查询车辆数据的 Hook
 * @param routeId 路线ID（可以是 string、number 或 null）
 * @param skip 是否跳过查询（可选，默认为 false）
 * @returns 返回车辆数据和选项
 */
export const useTmsVehiclesByRoute = (
    routeId: string | number | null | undefined,
    skip: boolean = false
) => {
    // 使用Hook根据路线获取司机和承运商ID
    const { selectedCarrierId } = useTmsDriversByRoute(routeId?.toString());
    
    // 使用Hook进行Route搜索，获取 route 信息
    const { getRouteWithCarrierInfoById } = useTmsSearch();

    // 根据 routeId 从搜索缓存中获取对应的 route 信息，用于获取 carrierId、routeType 等
    const [selectedRoute, setSelectedRoute] = useState<{
        id: string;
        routeName: string;
        routeType?: number;
        startWarehouseId: number;
        carrierId: number;
        businessType?: number;
        carrierSourceType?: number | null;
        carrierName?: string | null;
    } | null>(null);
    
    useEffect(() => {
        // 忽略过期响应：routeId 快速切换（如选中后又清空）时，旧请求可能晚于新状态落地，
        // 用旧路线数据把已重置的 selectedRoute 覆盖回去
        let cancelled = false;
        const fetchRoute = async () => {
            if (!routeId) {
                setSelectedRoute(null);
                return;
            }
            const routeIdNum = typeof routeId === 'string' ? parseInt(routeId) : routeId;
            if (isNaN(routeIdNum)) {
                setSelectedRoute(null);
                return;
            }
            const route = await getRouteWithCarrierInfoById(routeIdNum);
            if (!cancelled) {
                setSelectedRoute(route);
            }
        };
        if (!skip) {
            fetchRoute();
        }
        return () => {
            cancelled = true;
        };
    }, [routeId, getRouteWithCarrierInfoById, skip]);

    // 获取 carrierId：优先使用 selectedCarrierId，如果没有则使用 selectedRoute 的 carrierId
    const carrierIdForQuery = useMemo(() => {
        return selectedCarrierId || selectedRoute?.carrierId || null;
    }, [selectedCarrierId, selectedRoute?.carrierId]);

    // 根据 carrierId 查询车辆数据（选中 route 后自动查询）
    // status: 1 表示 AVAILABLE（可用状态），不查询 SUSPENDED（停用）状态的车辆
    const { data: vehiclesData, loading: vehiclesLoading } = useQuery<TmsVehiclesWithModelResponse>(GET_TMS_VEHICLES_WITH_MODEL, {
        variables: {
            carrierId: carrierIdForQuery ? Number(carrierIdForQuery) : null,
            status: TmsVehicleStatus.AVAILABLE, // 只查询可用状态的车辆，不查询 SUSPENDED 状态的车辆
            routeType: selectedRoute?.routeType
        },
        // 当 carrierId 为空时或 skip 为 true 时，不查询（避免查询所有车辆）
        skip: !carrierIdForQuery || skip
    });

    // 生成车辆选项
    const vehicleOptions = useMemo(() => {
        // 显示该承运商下的所有车辆（无论司机是否绑定了车辆）
        // 后端已支持通过 carrierId 过滤，直接使用返回的数据
        if (vehiclesData?.getTmsVehiclesWithModel) {
            return vehiclesData.getTmsVehiclesWithModel
                .map(vehicle => ({
                    label: vehicle.licensePlate || '',
                    value: vehicle.id.toString()
                }));
        }
        
        // 如果没有车辆数据，返回空数组
        return [];
    }, [vehiclesData]);

    return {
        vehiclesData,
        vehicleOptions,
        vehiclesLoading,
        selectedCarrierId,
        selectedRoute,
        carrierIdForQuery
    };
};

